import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    MapPin,
    Users,
    ShieldAlert,
    Flag,
    Plus,
    Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Row, SectionTitle, GradientButton } from "@/screens/_shared";
import {
    addEmergencyContact,
    getBlocked,
    getEmergencyContacts,
    removeEmergencyContact,
    toggleBlocked,
} from "@/lib/monetizationStore";
import { useMoneVersion } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const SafetyCenter = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useMoneVersion();
    const [panicState, setPanicState] = useState({
        active: false,
        sending: false,
        mapLink: null,
        delivery: [],
        error: null,
    });
    const [shareOn, setShareOn] = useState(false);
    const [shareTimer, setShareTimer] = useState(null);
    const [lastShareLink, setLastShareLink] = useState(null);

    const contacts = useMemo(() => (user ? getEmergencyContacts(user.id) : []), [user, v]);
    const blocked  = useMemo(() => (user ? getBlocked(user.id) : []), [user, v]);

    const sendPanic = async () => {
        if (!user) return;
        setPanicState((s) => ({ ...s, sending: true, error: null }));
        const numbers = (contacts || []).map((c) => c.phone).filter(Boolean);
        try {
            const pos = await new Promise((resolve, reject) => {
                if (!navigator.geolocation) {
                    reject(new Error("Geolocation not available"));
                    return;
                }
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                });
            });
            const { latitude, longitude } = pos.coords;
            const { data } = await axios.post(`${BACKEND_URL}/api/safety/panic`, {
                userId: user.id,
                userName: user.fullName || user.username || "A Neyra user",
                lat: latitude,
                lng: longitude,
                contacts: numbers,
            });
            setPanicState({
                active: true,
                sending: false,
                mapLink: data?.mapLink || `https://www.google.com/maps?q=${latitude},${longitude}`,
                delivery: data?.delivery || [],
                error: null,
            });
        } catch (err) {
            setPanicState((s) => ({
                ...s,
                sending: false,
                error: err?.message || "Could not send alert",
            }));
        }
    };

    const cancelPanic = () => {
        setPanicState({ active: false, sending: false, mapLink: null, delivery: [], error: null });
    };

    const toggleShare = () => {
        if (shareOn) {
            if (shareTimer) clearInterval(shareTimer);
            setShareTimer(null);
            setShareOn(false);
            setLastShareLink(null);
            return;
        }
        // Push location every 30s — best-effort, persists last link locally.
        const tick = () => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition((p) => {
                const link = `https://www.google.com/maps?q=${p.coords.latitude},${p.coords.longitude}`;
                setLastShareLink(link);
                try {
                    localStorage.setItem(
                        `neyra.liveLocation.${user?.id || "anon"}`,
                        JSON.stringify({ link, at: Date.now() })
                    );
                } catch {}
            }, () => {}, { enableHighAccuracy: true, timeout: 10000 });
        };
        tick();
        const id = setInterval(tick, 30_000);
        setShareTimer(id);
        setShareOn(true);
    };

    return (
        <div className="screen" data-testid="safety-screen">
            <PageHeader title="Safety Center" />
            <div className="flex-1 overflow-y-auto no-scrollbar pb-8">
                <div className="px-4 pt-4">
                    <div className="grid grid-cols-1 gap-3">
                        <PanicCard
                            state={panicState}
                            onTrigger={sendPanic}
                            onCancel={cancelPanic}
                            hasContacts={contacts.length > 0}
                        />
                        <ShareLocationCard active={shareOn} onToggle={toggleShare} link={lastShareLink} />
                    </div>
                </div>

                <SectionTitle>Emergency contacts</SectionTitle>
                <div className="px-4">
                    {contacts.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground px-2 py-2">
                            No emergency contacts yet. Add the people we should alert.
                        </p>
                    ) : (
                        <ul className="space-y-2" data-testid="safety-contacts-list">
                            {contacts.map((c) => (
                                <li
                                    key={c.id}
                                    data-testid={`safety-contact-${c.id}`}
                                    className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card"
                                >
                                    <InitialAvatar name={c.name} size={40} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13.5px] font-semibold truncate">{c.name}</div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">
                                            {c.phone}
                                        </div>
                                    </div>
                                    <button
                                        data-testid={`safety-contact-remove-${c.id}`}
                                        onClick={() => removeEmergencyContact(user.id, c.id)}
                                        className="h-9 w-9 rounded-lg text-destructive hover:bg-destructive/10 flex items-center justify-center"
                                        aria-label="Remove"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <AddContactSection userId={user?.id} />
                </div>

                <SectionTitle>Safety tips</SectionTitle>
                <div className="px-4 space-y-2">
                    <Tip title="Meet in public first" body="For first meetings, choose a public, well-lit location." />
                    <Tip title="Share your plans" body="Let a trusted contact know your location and timing." />
                    <Tip title="Trust your gut" body="If something feels off, leave and report the user." />
                </div>

                <SectionTitle>Blocked users</SectionTitle>
                <div className="px-4">
                    {blocked.length === 0 ? (
                        <p className="text-[12.5px] text-muted-foreground px-2 py-2">
                            You haven't blocked anyone.
                        </p>
                    ) : (
                        <ul className="space-y-2" data-testid="safety-blocked-list">
                            {blocked.map((b) => (
                                <li key={b.id} className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card">
                                    <InitialAvatar name={b.name} size={40} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13.5px] font-semibold truncate">{b.name}</div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">@{b.username}</div>
                                    </div>
                                    <button
                                        data-testid={`safety-unblock-${b.id}`}
                                        onClick={() => toggleBlocked(user.id, b)}
                                        className="h-9 px-3 rounded-lg bg-secondary text-[11.5px] font-medium"
                                    >
                                        Unblock
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <SectionTitle>Report</SectionTitle>
                <div className="px-2">
                    <Row icon={Flag} label="Report a user" onClick={() => navigate("/report")} testId="safety-report-user" />
                    <Row icon={Flag} label="Report an event" onClick={() => navigate("/report")} testId="safety-report-event" />
                    <Row icon={Flag} label="Report a service" onClick={() => navigate("/report")} testId="safety-report-service" />
                </div>
            </div>
        </div>
    );
};

const PanicCard = ({ state, onTrigger, onCancel, hasContacts }) => (
    <div
        data-testid="safety-panic-card"
        className={[
            "p-4 rounded-2xl border transition-colors",
            state.active ? "border-destructive bg-destructive/10" : "border-border bg-card",
        ].join(" ")}
    >
        <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-full bg-destructive/15 text-destructive flex items-center justify-center">
                <ShieldAlert size={20} />
            </span>
            <div className="flex-1 min-w-0">
                <div className="font-display text-[14px] font-semibold">Panic button</div>
                <div className="text-[11.5px] text-muted-foreground">
                    {hasContacts
                        ? "Sends an SMS with your live map link to your emergency contacts."
                        : "Add at least one emergency contact to enable."}
                </div>
            </div>
            {state.active ? (
                <button
                    data-testid="safety-panic-cancel"
                    onClick={onCancel}
                    className="h-10 px-4 rounded-full bg-secondary text-[12px] font-semibold"
                >
                    Cancel
                </button>
            ) : (
                <button
                    data-testid="safety-panic-trigger"
                    onClick={onTrigger}
                    disabled={!hasContacts || state.sending}
                    className="h-10 px-4 rounded-full bg-destructive text-destructive-foreground text-[12px] font-semibold shadow-card active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {state.sending ? "Sending…" : "Trigger"}
                </button>
            )}
        </div>
        {state.error && (
            <p className="mt-3 text-[12px] text-destructive font-medium" data-testid="safety-panic-error">
                {state.error}
            </p>
        )}
        {state.active && state.mapLink && (
            <div className="mt-3 text-[12px] space-y-1" data-testid="safety-panic-active">
                <p className="text-destructive font-medium">
                    Alert sent to {state.delivery?.length || 0} contact{state.delivery?.length === 1 ? "" : "s"}.
                </p>
                <a
                    href={state.mapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary font-semibold underline"
                    data-testid="safety-panic-map-link"
                >
                    <MapPin size={11} /> Open my location
                </a>
            </div>
        )}
    </div>
);

const ShareLocationCard = ({ active, onToggle, link }) => (
    <div data-testid="safety-share-location" className="p-4 rounded-2xl border border-border bg-card">
        <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <MapPin size={20} />
            </span>
            <div className="flex-1 min-w-0">
                <div className="font-display text-[14px] font-semibold">Share live location</div>
                <div className="text-[11.5px] text-muted-foreground">
                    {active ? "Sharing — link refreshes every 30s" : "Turn on when you need eyes on you"}
                </div>
            </div>
            <button
                type="button"
                data-testid="safety-share-toggle"
                onClick={onToggle}
                role="switch"
                aria-checked={active}
                className={["relative h-7 w-12 rounded-full transition-colors", active ? "bg-primary" : "bg-muted"].join(" ")}
            >
                <span
                    className={[
                        "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-soft transition-[left]",
                        active ? "left-[22px]" : "left-0.5",
                    ].join(" ")}
                />
            </button>
        </div>
        {active && link && (
            <a
                href={link}
                target="_blank"
                rel="noreferrer"
                data-testid="safety-share-current-link"
                className="mt-3 inline-flex items-center gap-1 text-[12px] text-primary font-semibold underline"
            >
                <MapPin size={11} /> Current location link
            </a>
        )}
    </div>
);

const Tip = ({ title, body }) => (
    <div className="p-3.5 rounded-2xl border border-border bg-card">
        <div className="font-display text-[13.5px] font-semibold">{title}</div>
        <p className="text-[11.5px] text-muted-foreground mt-0.5">{body}</p>
    </div>
);

const AddContactSection = ({ userId }) => {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");

    const save = () => {
        if (!name.trim() || !phone.trim()) return;
        addEmergencyContact(userId, { name: name.trim(), phone: phone.trim() });
        setName("");
        setPhone("");
        setOpen(false);
    };

    const pickFromPhonebook = async () => {
        // Use the Contact Picker API where available; otherwise, prompt to add manually.
        if (navigator.contacts && navigator.contacts.select) {
            try {
                const picks = await navigator.contacts.select(["name", "tel"], { multiple: false });
                const c = picks?.[0];
                if (c) {
                    addEmergencyContact(userId, {
                        name: (c.name || [])[0] || "Contact",
                        phone: (c.tel || [])[0] || "",
                    });
                }
                return;
            } catch { /* fall through to manual */ }
        }
        setOpen(true);
    };

    return (
        <div className="mt-3" data-testid="safety-add-contact">
            {!open ? (
                <div className="flex items-center gap-2">
                    <button
                        data-testid="safety-add-contact-open"
                        onClick={() => setOpen(true)}
                        className="flex-1 h-12 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 text-primary font-medium text-[13px] inline-flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                        <Plus size={15} />
                        Add manually
                    </button>
                    <button
                        data-testid="safety-add-contact-picker"
                        onClick={pickFromPhonebook}
                        className="h-12 px-4 rounded-2xl bg-secondary text-secondary-foreground font-medium text-[13px] inline-flex items-center gap-1.5 active:scale-[0.98]"
                    >
                        <Users size={15} />
                        From phone
                    </button>
                </div>
            ) : (
                <div className="p-3 rounded-2xl border border-border bg-card space-y-2.5">
                    <input
                        data-testid="safety-add-contact-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Contact name"
                        className="w-full h-11 px-3 rounded-xl bg-secondary outline-none text-[14px]"
                    />
                    <input
                        data-testid="safety-add-contact-phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Phone number (e.g. +234…)"
                        inputMode="tel"
                        className="w-full h-11 px-3 rounded-xl bg-secondary outline-none text-[14px]"
                    />
                    <div className="flex gap-2">
                        <button
                            data-testid="safety-add-contact-cancel"
                            onClick={() => setOpen(false)}
                            className="flex-1 h-11 rounded-xl bg-secondary text-[13px] font-medium"
                        >
                            Cancel
                        </button>
                        <div className="flex-1">
                            <GradientButton testId="safety-add-contact-save" onClick={save} tall={false}>
                                Save
                            </GradientButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SafetyCenter;
